import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import { api } from '../App';

const BackgroundTaskContext = createContext(null);

export function useBackgroundTasks() {
  return useContext(BackgroundTaskContext);
}

export function BackgroundTaskProvider({ children }) {
  const [tasks, setTasks] = useState([]);
  const tasksRef = useRef(tasks);
  const navigateRef = useRef(null);
  const pollingRef = useRef(false);
  const timerRef = useRef(null);

  // Keep ref in sync with state
  useEffect(() => { tasksRef.current = tasks; }, [tasks]);

  const setNavigate = useCallback((nav) => { navigateRef.current = nav; }, []);

  const addTask = useCallback((task) => {
    setTasks(prev => {
      const filtered = prev.filter(t => t.id !== task.id);
      return [...filtered, { ...task, status: 'processing', addedAt: Date.now() }];
    });
    toast.info(`${task.title} is processing. You can navigate away — we'll notify you when it's ready.`, { duration: 4000 });
  }, []);

  const removeTask = useCallback((taskId) => {
    setTasks(prev => prev.filter(t => t.id !== taskId));
  }, []);

  const acknowledgeTask = useCallback((taskId) => {
    setTasks(prev => prev.filter(t => t.id !== taskId));
  }, []);

  // Single sequential polling loop — no overlapping callbacks
  const pollOnce = useCallback(async () => {
    if (pollingRef.current) return;
    pollingRef.current = true;

    try {
      const currentTasks = tasksRef.current;
      const active = currentTasks.filter(t => t.status === 'processing');

      for (const task of active) {
        try {
          const res = await api.get(task.pollUrl);
          const status = res.data.status;

          if (status === 'complete' || status === 'done' || status === 'completed') {
            setTasks(prev => prev.map(t => t.id === task.id ? { ...t, status: 'complete', result: res.data } : t));
            toast.success(`${task.title} is ready!`, {
              duration: Infinity,
              action: {
                label: 'View Results',
                onClick: () => {
                  if (task.resultRoute && navigateRef.current) navigateRef.current(task.resultRoute);
                  setTasks(prev => prev.filter(t => t.id !== task.id));
                },
              },
              onDismiss: () => { setTasks(prev => prev.filter(t => t.id !== task.id)); },
            });
          } else if (status === 'error' || status === 'failed') {
            setTasks(prev => prev.map(t => t.id === task.id ? { ...t, status: 'error' } : t));
            toast.error(`${task.title} failed. Please try again.`, {
              duration: 8000,
              onDismiss: () => setTasks(prev => prev.filter(t => t.id !== task.id)),
            });
          }
        } catch (e) {
          // Transient error — keep polling
        }
      }
    } finally {
      pollingRef.current = false;
    }
  }, []);

  // Schedule polling: single setTimeout chain, re-evaluated on tasks change
  useEffect(() => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }

    const hasActive = tasks.some(t => t.status === 'processing');
    if (!hasActive) return;

    const schedule = () => {
      timerRef.current = setTimeout(async () => {
        await pollOnce();
        // Check if still has active tasks after poll
        const stillActive = tasksRef.current.some(t => t.status === 'processing');
        if (stillActive) schedule();
      }, 3000);
    };
    schedule();

    return () => { if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; } };
  }, [tasks, pollOnce]);

  const processingCount = tasks.filter(t => t.status === 'processing').length;
  const value = { tasks, addTask, removeTask, acknowledgeTask, processingCount, setNavigate };

  return (
    <BackgroundTaskContext.Provider value={value}>
      {children}
    </BackgroundTaskContext.Provider>
  );
}
